import React, { useState, useEffect } from 'react';
import secretsService from '../services/secretsService';
import './Secrets.css';

function Secrets({ isOpen, onClose }) {
  const [secrets, setSecrets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newSecretKey, setNewSecretKey] = useState('');
  const [newSecretValue, setNewSecretValue] = useState('');
  const [visibleSecrets, setVisibleSecrets] = useState(new Set());
  const [addingSecret, setAddingSecret] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadSecrets();
    }
  }, [isOpen]);

  const loadSecrets = async () => {
    setLoading(true);
    setError('');
    try {
      const fetchedSecrets = await secretsService.getSecrets();
      setSecrets(fetchedSecrets);
    } catch (err) {
      setError('Failed to load secrets: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSecret = async () => {
    if (!newSecretKey.trim() || !newSecretValue.trim()) {
      setError('Both key and value are required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await secretsService.putSecret(newSecretKey.trim(), newSecretValue.trim());
      setSuccess('Secret added successfully!');
      setNewSecretKey('');
      setNewSecretValue('');
      setAddingSecret(false);
      await loadSecrets();
    } catch (err) {
      setError('Failed to add secret: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSecret = async (secretKey) => {
    if (!window.confirm(`Are you sure you want to delete the secret "${secretKey}"?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await secretsService.deleteSecret(secretKey);
      setSuccess('Secret deleted successfully!');
      await loadSecrets();
    } catch (err) {
      setError('Failed to delete secret: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSecretVisibility = (secretKey) => {
    setVisibleSecrets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(secretKey)) {
        newSet.delete(secretKey);
      } else {
        newSet.add(secretKey);
      }
      return newSet;
    });
  };

  const maskSecret = (value) => {
    return '*'.repeat(Math.min(value.length, 20));
  };

  if (!isOpen) return null;

  return (
    <div className="secrets-modal-overlay" onClick={onClose}>
      <div className="secrets-modal" onClick={(e) => e.stopPropagation()}>
        <div className="secrets-header">
          <h2>🔐 Secrets Management</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="secrets-content">
          {error && <div className="secrets-error">{error}</div>}
          {success && <div className="secrets-success">{success}</div>}

          <div className="secrets-section">
            <div className="secrets-section-header">
              <h3>Your Secrets</h3>
              <button 
                className="add-secret-button"
                onClick={() => setAddingSecret(!addingSecret)}
                disabled={loading}
              >
                {addingSecret ? '✖ Cancel' : '➕ Add Secret'}
              </button>
            </div>

            {addingSecret && (
              <div className="add-secret-form">
                <div className="form-group">
                  <label>Secret Key (Name):</label>
                  <input
                    type="text"
                    value={newSecretKey}
                    onChange={(e) => setNewSecretKey(e.target.value)}
                    placeholder="e.g., API_KEY, DATABASE_PASSWORD"
                    className="secrets-input"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>Secret Value:</label>
                  <input
                    type="password"
                    value={newSecretValue}
                    onChange={(e) => setNewSecretValue(e.target.value)}
                    placeholder="Enter secret value"
                    className="secrets-input"
                    disabled={loading}
                  />
                </div>
                <button 
                  className="save-secret-button"
                  onClick={handleAddSecret}
                  disabled={loading || !newSecretKey.trim() || !newSecretValue.trim()}
                >
                  {loading ? '⏳ Saving...' : '💾 Save Secret'}
                </button>
              </div>
            )}

            <div className="secrets-list">
              {loading && secrets.length === 0 ? (
                <div className="secrets-loading">⏳ Loading secrets...</div>
              ) : secrets.length === 0 ? (
                <div className="secrets-empty">
                  <p>No secrets found.</p>
                  <p>Click "Add Secret" to create your first secret.</p>
                </div>
              ) : (
                secrets.map((secret) => (
                  <div key={secret.secretKey} className="secret-item">
                    <div className="secret-item-header">
                      <div className="secret-key">🔑 {secret.secretKey}</div>
                      <div className="secret-actions">
                        <button
                          className="toggle-visibility-button"
                          onClick={() => toggleSecretVisibility(secret.secretKey)}
                          title={visibleSecrets.has(secret.secretKey) ? 'Hide value' : 'Show value'}
                        >
                          {visibleSecrets.has(secret.secretKey) ? '👁️' : '👁️‍🗨️'}
                        </button>
                        <button
                          className="delete-secret-button"
                          onClick={() => handleDeleteSecret(secret.secretKey)}
                          disabled={loading}
                          title="Delete secret"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <div className="secret-value">
                      <span className="secret-value-label">Value:</span>
                      <code className="secret-value-text">
                        {visibleSecrets.has(secret.secretKey) 
                          ? secret.secretValue 
                          : maskSecret(secret.secretValue)}
                      </code>
                    </div>
                    {secret.updatedAt && (
                      <div className="secret-metadata">
                        Last updated: {new Date(secret.updatedAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="secrets-info">
            <h4>ℹ️ About Secrets</h4>
            <ul>
              <li>Secrets are stored securely and encrypted in DynamoDB</li>
              <li>Only you can access your secrets</li>
              <li>Secrets can be used by MCP tools when they are executed</li>
              <li>Click the eye icon to reveal/hide secret values</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Secrets;
